import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '../api/client';
import { campaignsApi, type Campaign } from '../api/campaigns';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  EmptyState,
  FormField,
  Input,
  LoadingState,
  PageHeader,
  PageLayout,
  RowActions,
  Section,
  StatusChip,
  Table,
} from '../components';
import { useI18n } from '../i18n';
import { useSession } from '../auth/session-context';

/**
 * Kampagnenübersicht.
 *
 * Auf einen Blick verständlich: Name, Beschreibung, ob die Kampagne läuft und
 * wie die Datensätze verteilt sind. Mehr steht hier bewusst nicht - Details
 * gehören in die Kampagne selbst.
 *
 * Der Kundenadmin sieht alle Kampagnen, ein Caller nur die für ihn
 * freigegebenen.
 */
export function CampaignsPage(): JSX.Element {
  const { t, tError } = useI18n();
  const { principal, handleApiError } = useSession();
  const nameId = useId();

  const isAdmin = principal?.kind === 'tenant_user' && principal.isAdmin;

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [name, setName] = useState('');
  const [anlegenOffen, setAnlegenOffen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [geladen, setGeladen] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      const response = isAdmin ? await campaignsApi.list(showArchived) : await campaignsApi.listMine();
      setCampaigns(response.campaigns);
    } catch (caught) {
      handleApiError(caught);
      setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
    } finally {
      setBusy(false);
      setGeladen(true);
    }
  }, [handleApiError, isAdmin, showArchived, tError]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = (event: FormEvent): void => {
    event.preventDefault();
    void (async () => {
      setError(null);
      setBusy(true);
      try {
        await campaignsApi.create(name);
        setName('');
        setAnlegenOffen(false);
        await load();
      } catch (caught) {
        handleApiError(caught);
        setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <PageLayout>
      <PageHeader
        title={t('campaigns.title')}
        actions={
          isAdmin ? (
            <Button variant="primary" onClick={() => setAnlegenOffen((offen) => !offen)}>
              {t('campaigns.create')}
            </Button>
          ) : undefined
        }
      />

      {error ? <Alert tone="error">{error}</Alert> : null}

      {isAdmin && anlegenOffen ? (
        <Card>
          <Section title={t('campaigns.create')}>
            <form onSubmit={create} className="form form-inline">
              <FormField label={t('campaigns.name')} htmlFor={nameId}>
                <Input id={nameId} value={name} onChange={setName} />
              </FormField>
              <Button type="submit" variant="primary" disabled={busy || name.trim() === ''}>
                {t('action.create')}
              </Button>
              <Button variant="secondary" onClick={() => setAnlegenOffen(false)}>
                {t('action.cancel')}
              </Button>
            </form>
          </Section>
        </Card>
      ) : null}

      {isAdmin ? (
        <Checkbox checked={showArchived} onChange={setShowArchived} label={t('campaigns.showArchived')} />
      ) : null}

      {!geladen ? <LoadingState /> : null}

      {geladen ? (
        <Card>
          {campaigns.length === 0 ? (
            <EmptyState
              title={t('campaigns.empty')}
              description={isAdmin ? t('dashboard.quickStartText') : undefined}
              action={
                isAdmin ? (
                  <Button variant="primary" onClick={() => setAnlegenOffen(true)}>
                    {t('campaigns.create')}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <Table
              label={t('campaigns.title')}
              headers={[
                t('campaigns.name'),
                t('campaigns.state.active'),
                t('campaign.overview.mode'),
                '',
              ]}
            >
              {campaigns.map((campaign) => (
                <tr key={campaign.id}>
                  <td>
                    <strong>{campaign.name}</strong>
                    {campaign.description ? <div className="field-hint">{campaign.description}</div> : null}
                  </td>
                  <td>
                    <StatusChip
                      tone={campaign.archivedAt ? 'neutral' : campaign.isActive ? 'erfolg' : 'warnung'}
                    >
                      {campaign.archivedAt
                        ? t('campaigns.state.archived')
                        : campaign.isActive
                          ? t('campaigns.state.active')
                          : t('campaigns.state.stopped')}
                    </StatusChip>
                  </td>
                  <td>
                    <StatusChip tone="info">{t(`campaigns.mode.${campaign.campaignMode}`)}</StatusChip>
                  </td>
                  <RowActions>
                    <Link className="btn btn-secondary" to={`/kampagnen/${campaign.id}`}>
                      {t('campaigns.open')}
                    </Link>
                  </RowActions>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      ) : null}
    </PageLayout>
  );
}
